import {
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable, Subscription } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';
import { MenuItem } from 'primeng/api';
import { AuthenticationService } from '../_services/authentication.service';
import { AdminService } from '../_services/admin.service';
import { NavigationEnd, Router } from '@angular/router';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
})
export class NavigationComponent implements OnInit, OnDestroy {
  profileitems: MenuItem[];
  @Input() user: any = this.authenticationService.userValue;
  user$ = this.authenticationService.user;
  sideMenuItems: MenuItem[] = [];
  isAdmin = false;
  profileName = '';
  profileEmail = '';
  profileInitials = '';
  profileRole = '';
  sidebarVisible: boolean = true;

  visibleSidebar = false;
  isAuthRoute = false;
  isMobile = false;
  isDesktopSidebarCollapsed = false;
  private routeSubscription?: Subscription;
  private userSubscription?: Subscription;
  private readonly authRoutes = ['/login', '/forgot-password'];
  menuItems = [
    { label: 'Home', routerLink: '/home' },
    { label: 'About', routerLink: '/about' },
  ];

  showFiller = false;
  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    public authenticationService: AuthenticationService,
    private adminService: AdminService,
    private router: Router
  ) {
    this.user = this.authenticationService.userValue;
    this.updateAuthRouteState(this.router.url);
    this.updateSideMenuItems(this.user);

    this.profileitems = [
      {
        label: 'Logout',
        command: () => {
          this.authenticationService.logout();
        },
      },
    ];
  }
  ngOnInit() {
    this.checkScreenWidth();
    this.routeSubscription = this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      )
      .subscribe((event) => {
        this.updateAuthRouteState(event.urlAfterRedirects);
      });

    // NavigationComponent wraps <router-outlet> in app.component.html and
    // is constructed exactly once for the app's lifetime. Logging in
    // navigates via router.navigateByUrl() rather than a full page reload,
    // so without this subscription sideMenuItems stayed frozen at whatever
    // it was built as in the constructor - permanently empty for anyone
    // who logged in during the current page load, since the sidebar
    // wouldn't show any links until a manual refresh re-ran the
    // constructor with the now-authenticated state.
    this.userSubscription = this.authenticationService.user.subscribe(
      (user) => {
        this.user = user;
        this.isAdmin = false;
        this.updateSideMenuItems(user);
        if (user) {
          // Menu gating only — every admin API also enforces this server-side.
          this.adminService.resetAccessCache();
          this.adminService.isAdmin().subscribe((isAdmin) => {
            this.isAdmin = isAdmin;
            this.profileRole = isAdmin ? 'Administrator' : 'Operator';
            this.updateSideMenuItems(this.user);
          });
          this.loadProfile();
        } else {
          this.clearProfile();
        }
      }
    );
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
    this.userSubscription?.unsubscribe();
  }

  private loadProfile(): void {
    this.adminService.profile().subscribe({
      next: (profile) => {
        const name =
          [profile?.first_name, profile?.last_name]
            .filter(Boolean)
            .join(' ')
            .trim() ||
          profile?.full_name ||
          profile?.username ||
          profile?.email ||
          '';
        this.profileName = name;
        this.profileEmail = profile?.email || '';
        this.profileInitials = this.initialsFor(name || profile?.email || '?');
      },
      // A profile fetch failure must never blank the whole shell.
      error: () => this.clearProfile(),
    });
  }

  private clearProfile(): void {
    this.profileName = '';
    this.profileEmail = '';
    this.profileInitials = '';
    this.profileRole = '';
  }

  private initialsFor(source: string): string {
    const parts = source.trim().split(/[\s@._-]+/).filter(Boolean);
    if (parts.length === 0) {
      return '?';
    }
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  private updateSideMenuItems(user: any): void {
    this.sideMenuItems = user
      ? [
          { label: 'Home', routerLink: '/', icon: 'pi pi-fw pi-home' },
          {
            label: 'Vehicles',
            routerLink: 'vehicles',
            icon: 'pi pi-fw pi-truck',
          },
          {
            label: 'Technical',
            routerLink: 'technical',
            icon: 'pi pi-fw pi-cog',
          },
          {
            label: 'Active Issues',
            routerLink: 'active-issues',
            icon: 'pi pi-fw pi-list',
          },
          {
            label: 'Shortcuts',
            routerLink: 'hotkeys',
            icon: 'pi pi-fw pi-key',
          },
          ...(this.isAdmin
            ? [
                {
                  label: 'Admin',
                  routerLink: 'admin',
                  icon: 'pi pi-fw pi-shield',
                },
              ]
            : []),
        ]
      : [];
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.checkScreenWidth();
  }

  checkScreenWidth() {
    this.isMobile = window.innerWidth <= 768;
  }

  toggleSidebar() {
    if (this.isMobile) {
      this.visibleSidebar = !this.visibleSidebar;
      return;
    }

    this.isDesktopSidebarCollapsed = !this.isDesktopSidebarCollapsed;
  }

  hideSidebar() {
    this.visibleSidebar = false;
  }

  private updateAuthRouteState(url: string): void {
    const path = this.normalizeRouteUrl(url);
    this.isAuthRoute = this.authRoutes.some(
      (route) => path === route || path.startsWith(`${route}/`)
    );

    if (this.isAuthRoute) {
      this.visibleSidebar = false;
    }
  }

  private normalizeRouteUrl(url: string): string {
    const [withoutQuery] = (url || '').split('?');
    const path = withoutQuery.startsWith('/#')
      ? withoutQuery.slice(2)
      : withoutQuery.split('#')[0];

    return path.startsWith('/') ? path : `/${path}`;
  }

  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(
      map((result) => result.matches),
      shareReplay(1)
    );

}
